
<template>
  <div>
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-4 md:mb-0">Browse Research Articles</h1>
      <div class="w-full md:w-auto flex flex-col sm:flex-row gap-4">
        <div class="relative">
          <select v-model="selectedCategory" @change="filterArticles" class="appearance-none pl-4 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 w-full">
            <option value="">All Categories</option>
            <option v-for="category in categories" :value="category" :key="category">{{ category }}</option>
          </select>
          <i class="fas fa-chevron-down absolute right-3 top-2.5 text-gray-400 pointer-events-none"></i>
        </div>
        <div class="relative">
          <select v-model="sortBy" @change="filterArticles" class="appearance-none pl-4 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 w-full">
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="title-asc">Title (A-Z)</option>
            <option value="title-desc">Title (Z-A)</option>
            <option value="views-desc">Most Viewed</option>
            <option value="citations-desc">Most Cited</option>
          </select>
          <i class="fas fa-chevron-down absolute right-3 top-2.5 text-gray-400 pointer-events-none"></i>
        </div>
      </div>
    </div>

    <div class="flex flex-col lg:flex-row gap-6">
      <!-- Filters Sidebar -->
      <div class="w-full lg:w-1/4">
        <div class="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div class="p-4 border-b border-gray-200 bg-gray-50">
            <div class="flex justify-between items-center">
              <h3 class="font-medium text-gray-800">Filters</h3>
              <button @click="resetFilters" class="text-sm text-green-600 hover:text-green-800">Reset All</button>
            </div>
          </div>
          <div class="p-4">
            <!-- Search within results -->
            <div class="mb-4">
              <div class="relative">
                <input 
                  type="text" 
                  v-model="searchWithinResults" 
                  @input="filterArticles" 
                  placeholder="Search within results..." 
                  class="pl-8 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 w-full"
                >
                <i class="fas fa-search absolute left-3 top-2.5 text-gray-400"></i>
              </div>
            </div>

            <!-- Open Access -->
            <div class="mb-4">
              <div class="flex items-center mb-2">
                <input 
                  type="checkbox" 
                  id="open-access" 
                  v-model="openAccessOnly" 
                  @change="filterArticles" 
                  class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                >
                <label for="open-access" class="ml-2 text-sm text-gray-700">Open Access Only</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Articles List -->
      <div class="w-full lg:w-3/4">
        <div class="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div class="p-4 border-b border-gray-200 bg-gray-50">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <p class="text-sm text-gray-600 mb-2 sm:mb-0">
                Showing <span class="font-medium">{{ filteredArticles.length }}</span> results
                <span v-if="searchWithinResults">for "<span class="font-medium">{{ searchWithinResults }}</span>"</span>
              </p>
              <div class="flex items-center">
                <span class="text-sm text-gray-600 mr-2">View:</span>
                <button @click="viewMode = 'list'" :class="['px-3 py-1 text-sm rounded-l', viewMode === 'list' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']">
                  <i class="fas fa-list"></i>
                </button>
                <button @click="viewMode = 'grid'" :class="['px-3 py-1 text-sm rounded-r', viewMode === 'grid' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']">
                  <i class="fas fa-th-large"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- List View -->
          <div v-if="viewMode === 'list'" class="divide-y divide-gray-200">
            <div v-for="article in paginatedArticles" :key="article.id" class="p-6 hover:bg-gray-50 transition duration-150">
              <div class="flex flex-col sm:flex-row">
                <div class="flex-1">
                    <router-link
                :to="`/article/${article.slug}`"
              >
                  <h3 class="text-lg font-semibold text-gray-800 mb-1 hover:text-green-600 cursor-pointer">
                    {{ article.title }}
                  </h3>
                  </router-link>
                  <p class="text-gray-600 text-sm mb-2">{{ article.authors.join(', ') }}</p>
                  <p class="text-gray-700 mb-3">{{ article.abstract.substring(0, 200) }}...</p>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="tag in article.tags" :key="tag" class="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">{{ tag }}</span>
                    <span v-if="article.openAccess" class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      <i class="fas fa-lock-open mr-1"></i> Open Access
                    </span>
                  </div>
                </div>
                <div class="sm:w-40 sm:pl-4 mt-4 sm:mt-0 flex sm:flex-col justify-between">
                  <div>
                    <span class="text-sm text-gray-500 block">{{ formatDate(article.publishedDate) }}</span>
                    <span class="text-xs text-gray-500 block mt-1">
                      <i class="fas fa-eye mr-1"></i> {{ article.views.toLocaleString() }} views
                    </span>
                    <span class="text-xs text-gray-500 block mt-1">
                      <i class="fas fa-quote-right mr-1"></i> {{ article.citations }} citations
                    </span>
                  </div>
                  <button @click="$emit('view-article', article.id)" class="text-sm text-green-600 hover:text-green-800 font-medium self-end">Read More</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Grid View -->
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6">
            <div v-for="article in paginatedArticles" :key="article.id" class="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition duration-200">
              <div class="p-4">
                <div class="flex justify-between items-start mb-2">
                  <span class="text-xs font-medium px-2 py-1 bg-green-100 text-green-800 rounded-full">{{ article.type }}</span>
                  <span class="text-xs text-gray-500">{{ formatDateShort(article.publishedDate) }}</span>
                </div>
                <h3 class="text-md font-semibold text-gray-800 mb-2 hover:text-green-600 cursor-pointer" @click="$emit('view-article', article.id)">
                  {{ article.title.substring(0, 80) }}{{ article.title.length > 80 ? '...' : '' }}
                </h3>
                <p class="text-gray-600 text-sm mb-3">{{ article.authors[0] }}{{ article.authors.length > 1 ? ' et al.' : '' }}</p>
                <div class="flex flex-wrap gap-1 mb-3">
                  <span v-for="(tag, index) in article.tags.slice(0, 2)" :key="tag" class="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">
                    {{ tag }}
                  </span>
                  <span v-if="article.tags.length > 2" class="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">
                    +{{ article.tags.length - 2 }} more
                  </span>
                </div>
                <div class="flex justify-between items-center text-xs text-gray-500">
                  <span><i class="fas fa-eye mr-1"></i> {{ article.views.toLocaleString() }}</span>
                  <span><i class="fas fa-quote-right mr-1"></i> {{ article.citations }}</span>
                  <span v-if="article.openAccess" class="text-green-600">
                    <i class="fas fa-lock-open mr-1"></i> Open
                  </span>
                </div>
              </div>
              <div class="bg-gray-50 px-4 py-2 border-t border-gray-200">
                <button @click="$emit('view-article', article.id)" class="w-full text-sm text-green-600 hover:text-green-800 font-medium text-center">
                  View Details
                </button>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center">
            <div class="text-sm text-gray-600 mb-4 sm:mb-0">
              Showing <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span> to 
              <span class="font-medium">{{ Math.min(currentPage * itemsPerPage, filteredArticles.length) }}</span> of 
              <span class="font-medium">{{ filteredArticles.length }}</span> results
            </div>
            <div class="flex space-x-1">
              <button 
                @click="currentPage = 1" 
                :disabled="currentPage === 1" 
                :class="['px-3 py-1 rounded', currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']"
              >
                <i class="fas fa-angle-double-left"></i>
              </button>
              <button 
                @click="currentPage--" 
                :disabled="currentPage === 1" 
                :class="['px-3 py-1 rounded', currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']"
              >
                <i class="fas fa-angle-left"></i>
              </button>
              <button 
                v-for="page in visiblePages" 
                :key="page"
                @click="currentPage = page" 
                :class="['px-3 py-1 rounded', currentPage === page ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']"
              >
                {{ page }}
              </button>
              <button 
                @click="currentPage++" 
                :disabled="currentPage === totalPages" 
                :class="['px-3 py-1 rounded', currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']"
              >
                <i class="fas fa-angle-right"></i>
              </button>
              <button 
                @click="currentPage = totalPages" 
                :disabled="currentPage === totalPages" 
                :class="['px-3 py-1 rounded', currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']"
              >
                <i class="fas fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'BrowseView',
  props: {
    articles: Array,
    searchQuery: String
  },
  emits: ['view-article'],
  setup(props) {
    // Filter state
    const selectedCategory = ref('')
    const sortBy = ref('date-desc')
    const searchWithinResults = ref('')
    const openAccessOnly = ref(false)
    const currentPage = ref(1)
    const itemsPerPage = ref(10)
    const viewMode = ref('list')

    // Watch for external search query changes
    watch(() => props.searchQuery, (newQuery) => {
      searchWithinResults.value = newQuery
      filterArticles()
    })

    // Computed properties
    const categories = computed(() => {
      return [...new Set(props.articles.map(article => article.category))]
    })

    const filteredArticles = computed(() => {
  let result = [...props.articles]

  // ✅ Show only published articles
  result = result.filter(article => article.status === 'published')

  // Category filter
  if (selectedCategory.value) {
    result = result.filter(article => article.category === selectedCategory.value)
  }

  // Search within results
  if (searchWithinResults.value) {
    const searchTerm = searchWithinResults.value.toLowerCase()
    result = result.filter(article => 
      article.title.toLowerCase().includes(searchTerm) || 
      article.abstract.toLowerCase().includes(searchTerm) ||
      article.authors.some(author => author.toLowerCase().includes(searchTerm)) ||
      article.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    )
  }

  // Open access filter
  if (openAccessOnly.value) {
    result = result.filter(article => article.openAccess)
  }

  // Sorting
  switch (sortBy.value) {
    case 'date-desc':
      result.sort((a, b) => new Date(b.publishedDate) - new Date(a.publishedDate))
      break
    case 'date-asc':
      result.sort((a, b) => new Date(a.publishedDate) - new Date(b.publishedDate))
      break
    case 'title-asc':
      result.sort((a, b) => a.title.localeCompare(b.title))
      break
    case 'title-desc':
      result.sort((a, b) => b.title.localeCompare(a.title))
      break
    case 'views-desc':
      result.sort((a, b) => b.views - a.views)
      break
    case 'citations-desc':
      result.sort((a, b) => b.citations - a.citations)
      break
  }

  return result
})

    const totalPages = computed(() => {
      return Math.ceil(filteredArticles.value.length / itemsPerPage.value)
    })

    const paginatedArticles = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredArticles.value.slice(start, end)
    })

    const visiblePages = computed(() => {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages.value, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    })

    // Methods
    const filterArticles = () => {
      currentPage.value = 1
    }

    const resetFilters = () => {
      selectedCategory.value = ''
      searchWithinResults.value = ''
      openAccessOnly.value = false
      sortBy.value = 'date-desc'
      filterArticles()
    }

    const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(dateString).toLocaleDateString(undefined, options)
    }

    const formatDateShort = (dateString) => {
      return new Date(dateString).getFullYear()
    }

    return {
      // State
      selectedCategory,
      sortBy,
      searchWithinResults,
      openAccessOnly,
      currentPage,
      itemsPerPage,
      viewMode,
      // Computed
      categories,
      filteredArticles,
      paginatedArticles,
      totalPages,
      visiblePages,
      // Methods
      filterArticles,
      resetFilters,
      formatDate,
      formatDateShort
    }
  }
}
</script>

<style scoped>
.article-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}
</style>